import React, { useEffect, useRef, useState } from "react";
import { Lock, RefreshCw, ScanFace, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const FACE_MODEL_URL = `${process.env.PUBLIC_URL || ""}/vendor/face-api/weights`;
const DESKTOP_FACE_GATE_TIMEOUT_MS = 3000;
const MOBILE_FACE_GATE_TIMEOUT_MS = 6500;
let faceModelsPromise = null;

function clearLoginState() {
  localStorage.removeItem("ct_login_token");
  localStorage.removeItem("ct_login_user");
  localStorage.removeItem("ct_admin_auth");
  localStorage.removeItem("ct_2fa_token");
  localStorage.removeItem("ct_2fa_user");
  localStorage.removeItem("ct_email_otp_token");
}

function grabFrame(video) {
  if (!video?.videoWidth || !video?.videoHeight) return null;
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function getFaceApi() {
  return window.faceapi || window.faceApi || null;
}

function isLikelyMobileDevice() {
  const ua = String(navigator.userAgent || "");
  return /android|iphone|ipad|ipod|mobile|tablet/i.test(ua) || (navigator.maxTouchPoints > 1 && window.innerWidth < 1024);
}

function faceGateTimeoutMs() {
  return isLikelyMobileDevice() ? MOBILE_FACE_GATE_TIMEOUT_MS : DESKTOP_FACE_GATE_TIMEOUT_MS;
}

async function waitForFaceApi() {
  for (let i = 0; i < 40; i++) {
    const faceapi = getFaceApi();
    if (faceapi?.nets?.tinyFaceDetector) return faceapi;
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  throw new Error("Face scanner library is not loaded");
}

async function loadFaceModels() {
  if (!faceModelsPromise) {
    faceModelsPromise = waitForFaceApi().then(async faceapi => {
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(FACE_MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(FACE_MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(FACE_MODEL_URL),
      ]);
      return faceapi;
    });
  }
  return faceModelsPromise;
}

function distance(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function eyeScoreFromLandmarks(landmarks, box) {
  const leftEye = landmarks.getLeftEye();
  const rightEye = landmarks.getRightEye();
  if (leftEye.length < 6 || rightEye.length < 6 || !box?.width) return 0;

  const leftWidth = distance(leftEye[0], leftEye[3]);
  const rightWidth = distance(rightEye[0], rightEye[3]);
  const eyeGap = distance(leftEye[3], rightEye[0]);
  const eyeLevelDiff = Math.abs(leftEye[0].y - rightEye[3].y);
  const widthScore = Math.min(100, ((leftWidth + rightWidth) / box.width) * 520);
  const gapScore = Math.min(100, (eyeGap / box.width) * 340);
  const levelScore = Math.max(0, 100 - (eyeLevelDiff / box.height) * 650);
  return Math.round(widthScore * 0.45 + gapScore * 0.25 + levelScore * 0.3);
}

async function scanFaceFromVideo(video) {
  const faceapi = await loadFaceModels();
  const options = new faceapi.TinyFaceDetectorOptions({
    inputSize: isLikelyMobileDevice() ? 416 : 320,
    scoreThreshold: isLikelyMobileDevice() ? 0.58 : 0.68,
  });
  const detections = await faceapi
    .detectAllFaces(video, options)
    .withFaceLandmarks()
    .withFaceDescriptors();

  if (!detections.length) throw new Error("No real face detected. Keep your face centered.");
  if (detections.length > 1) throw new Error("Only one face is allowed for secure login.");

  const scan = detections[0];
  const box = scan.detection.box;
  const detectionScore = scan.detection.score || 0;
  const eyeScore = eyeScoreFromLandmarks(scan.landmarks, box);
  const frameArea = video.videoWidth * video.videoHeight;
  const faceArea = box.width * box.height;

  if (detectionScore < 0.72) throw new Error("Face scan is not clear enough. Move closer.");
  if (eyeScore < 55) throw new Error("Both eyes must be clearly visible.");
  if (faceArea / frameArea < 0.08) throw new Error("Move closer to the camera.");

  const canvas = grabFrame(video);
  if (!canvas) throw new Error("Camera frame not ready. Try again.");

  return {
    image: canvas.toDataURL("image/jpeg", 0.72),
    descriptor: Array.from(scan.descriptor).map(value => Number(value.toFixed(6))),
    detectionScore,
    eyeScore,
    landmarkCount: scan.landmarks.positions.length,
    faceCount: detections.length,
  };
}

async function scanFaceWithRetries(video, timeoutMs) {
  await loadFaceModels();
  const startedAt = Date.now();
  let lastError = null;

  while (Date.now() - startedAt < timeoutMs) {
    try {
      if (!video?.videoWidth || !video?.videoHeight) {
        throw new Error("Camera frame not ready");
      }
      return await scanFaceFromVideo(video);
    } catch (nextError) {
      lastError = nextError;
      await new Promise(resolve => setTimeout(resolve, isLikelyMobileDevice() ? 320 : 180));
    }
  }

  throw lastError || new Error("Secure sign-in timed out.");
}

export default function FaceLogin() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState("Checking secure sign-in...");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showFallbackLogin, setShowFallbackLogin] = useState(false);

  const stopCamera = () => {
    streamRef.current?.getTracks?.().forEach(track => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const startCamera = async () => {
    setError("");
    setStatus("Checking secure sign-in...");
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
      audio: false,
    });
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      await videoRef.current.play().catch(() => {});
    }
    setStatus("Checking secure sign-in...");
  };

  const showFallback = message => {
    setError(message || "");
    setStatus("Opening password login...");
    stopCamera();
    navigate("/credentials-login", { replace: true, state: { mode: "password" } });
  };

  const scanAndLogin = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    setShowFallbackLogin(false);
    try {
      if (!streamRef.current) await startCamera();
      setStatus("Checking secure sign-in...");

      const timeoutMs = faceGateTimeoutMs();
      const faceScan = await new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          reject(new Error("Secure sign-in timed out."));
        }, timeoutMs + 500);

        loadFaceModels()
          .then(() => new Promise(done => setTimeout(done, isLikelyMobileDevice() ? 450 : 180)))
          .then(() => scanFaceWithRetries(videoRef.current, timeoutMs))
          .then(result => {
            clearTimeout(timer);
            resolve(result);
          })
          .catch(nextError => {
            clearTimeout(timer);
            reject(nextError);
          });
      });

      setStatus("Verifying secure sign-in...");

      const res = await fetch(`${API_BASE}/api/face-login/identify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(faceScan),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Face recognition failed");
      }

      clearLoginState();
      localStorage.setItem("ct_2fa_token", data.tempToken);
      localStorage.setItem("ct_2fa_user", JSON.stringify(data.user));
      stopCamera();
      setStatus("Secure sign-in confirmed. Opening two factor OTP...");
      navigate(data.setupRequired ? "/two-factor-setup" : "/two-factor", { replace: true });
    } catch (nextError) {
      showFallback(nextError.message || "Face recognition failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    clearLoginState();
    loadFaceModels().catch(() => {});
    startCamera()
      .then(async () => {
        await loadFaceModels();
        if (!cancelled) setTimeout(scanAndLogin, 250);
      })
      .catch(err => {
        if (!cancelled) {
          showFallback(err.message || "Webcam permission denied");
        }
      });

    return () => {
      cancelled = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="min-h-screen flex items-center justify-center px-4" style={{ background: "#020202", color: "#f8fafc" }}>
      <section className="w-full max-w-[520px] rounded-xl p-7" style={{ background: "#080704", border: "1px solid rgba(247,201,72,.32)" }}>
        <div className="text-center mb-5">
          <ScanFace size={42} className="mx-auto mb-3 text-yellow-300" />
          <h1 className="text-2xl font-bold mb-2">Secure Sign-In</h1>
          <p className="text-sm text-slate-300">
            Checking secure sign-in. You can continue with credentials or scanner if verification is unavailable.
          </p>
        </div>

        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          aria-hidden="true"
          style={{ position: "fixed", left: -9999, top: 0, width: 180, height: 135, opacity: 0, pointerEvents: "none" }}
        />

        <div className="rounded-lg p-3 mb-4 text-sm" style={{ background: "#111827", border: "1px solid #334155" }}>
          <ShieldCheck size={15} className="inline mr-2" />
          {status}
        </div>

        {error && <div className="text-red-300 text-sm mb-4">{error}</div>}

        {showFallbackLogin && (
          <div className="rounded-lg p-3 mb-4 text-sm text-slate-300" style={{ background: "#111827", border: "1px solid #334155" }}>
            Secure sign-in was not completed. Choose another login method.
          </div>
        )}

        {showFallbackLogin && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button className="rounded py-3 font-semibold" style={{ background: "#1f2937" }} onClick={() => navigate("/credentials-login", { replace: true, state: { mode: "password" } })}>
            <Lock size={15} className="inline mr-2" />Credentials
          </button>
          <button className="rounded py-3 font-semibold" style={{ background: "#1f2937" }} onClick={() => navigate("/credentials-login", { replace: true, state: { mode: "scan" } })}>
            <RefreshCw size={15} className="inline mr-2" />Scanner
          </button>
        </div>
        )}
      </section>
    </main>
  );
}
